import React, { useState } from "react";
import "./LoginSignup.css";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { sendSMSOtp } from "../../utils/smsOtp";
import {
  getPendingAuthRedirect,
  savePendingAuthRedirect,
} from "../../utils/authRedirect";

const LoginSignup = () => {
  const [formData, setFormData] = useState({ phone: "" });
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [searchParams] = useSearchParams();
  const infoMessage = searchParams.get("message") || "";
  const navigate = useNavigate();
  const location = useLocation();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError("");
  };

  const normalizePhone = (phone) => {
    const digits = String(phone || "").replace(/\D/g, "");

    if (digits.length === 10) {
      return digits;
    }

    if (digits.length === 12 && digits.startsWith("91")) {
      return digits.slice(2);
    }

    throw new Error("Please enter a valid 10-digit phone number.");
  };

  const clearPendingAuth = () => {
    localStorage.removeItem("pendingEmail");
    localStorage.removeItem("pendingPhone");
    localStorage.removeItem("pendingOtpPurpose");
    localStorage.removeItem("pendingOtpMethod");
    localStorage.removeItem("pendingRegistrationData");
    localStorage.removeItem("pendingAuthRedirect");
  };

  const handleLogin = async (e) => {
    e.preventDefault();

    // Start loading state
    setIsSubmitting(true);
    setError("");
    setLoadingMessage("Sending OTP to your phone...");

    try {
      // Add small delay for better UX
      await new Promise(resolve => setTimeout(resolve, 300));

      const redirectPath = location.state?.from || getPendingAuthRedirect();
      clearPendingAuth();

      if (redirectPath) {
        savePendingAuthRedirect(redirectPath);
      }

      const normalizedPhone = normalizePhone(formData.phone);
      await sendSMSOtp(normalizedPhone, "login");

      localStorage.setItem("pendingPhone", normalizedPhone);
      localStorage.setItem("pendingOtpPurpose", "login");
      localStorage.setItem("pendingOtpMethod", "sms");
      
      // Add small delay before navigation for smooth transition
      setTimeout(() => {
        navigate("/verify-otp");
      }, 500);
      
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Failed to send OTP");
    } finally {
      // Stop loading state
      setIsSubmitting(false);
      setLoadingMessage("");
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-box">
        <h2 className="auth-title">Welcome Back</h2>
        {infoMessage && <div className="auth-info-message">{infoMessage}</div>}
        <form onSubmit={handleLogin}>
          <input
            type="tel"
            name="phone"
            placeholder="Phone Number (10 digits)"
            value={formData.phone}
            onChange={handleChange}
            required
          />
          {error && <p className="auth-error">{error}</p>}
          <button 
            type="submit" 
            className={`auth-button ${isSubmitting ? 'auth-button-loading' : ''}`}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <span className="button-content">
                <span className="spinner"></span>
                Sending SMS OTP...
              </span>
            ) : (
              "Send SMS OTP"
            )}
          </button>
        </form>
        <div className="auth-footer">
          <p>
            New user?{" "}
            <span
              onClick={() => navigate("/register", {
                state: { from: location.state?.from || getPendingAuthRedirect() },
              })}
            >
              Register
            </span>
          </p>
        </div>
      </div>
      
      {/* Loading Overlay */}
      {isSubmitting && (
        <div className="loading-overlay">
          <div className="loading-content">
            <div className="loading-spinner"></div>
            <p className="loading-message">{loadingMessage}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginSignup;
